import { GOOGLE_MAPS_API_KEY } from '@/shared/config/environment';

class GoogleMapsVerifier {
    private static instance: GoogleMapsVerifier;
    private isValid: boolean | null = null;
    private checkPromise: Promise<boolean> | null = null;

    private constructor() { }

    public static getInstance(): GoogleMapsVerifier {
        if (!GoogleMapsVerifier.instance) {
            GoogleMapsVerifier.instance = new GoogleMapsVerifier();
        }
        return GoogleMapsVerifier.instance;
    }

    /**
     * Vérifie si la clé API Google Maps est valide en effectuant un appel fetch test.
     * Le résultat est mis en cache pour éviter des appels répétés.
     */
    public async isApiKeyValid(): Promise<boolean> {
        // Si on a déjà le résultat, on le retourne
        if (this.isValid !== null) {
            return this.isValid;
        }

        // Si une vérification est déjà en cours, on retourne la promesse existante
        if (this.checkPromise) {
            return this.checkPromise;
        }

        // Sinon, on lance la vérification
        this.checkPromise = this.performCheck();
        return this.checkPromise;
    }

    private async performCheck(): Promise<boolean> {
        if (!GOOGLE_MAPS_API_KEY) {
            console.warn("❌ Clé API Google Maps manquante");
            this.isValid = false;
            return false;
        }

        try {
            // On tente de charger le script via fetch pour voir si on a une erreur HTTP immédiate (ex: 403)
            const response = await fetch(`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`);

            if (!response.ok) {
                console.error(`❌ Erreur fetch Google Maps: ${response.status} ${response.statusText}`);
                this.isValid = false;
                return false;
            }

            // Si le fetch réussit (200 OK), on considère que c'est bon pour l'instant
            // Note: Google peut renvoyer 200 avec un script d'erreur, donc gm_authFailure reste nécessaire
            this.isValid = true;
            return true;
        } catch (error) {
            console.warn("⚠️ Impossible de vérifier la clé API via fetch (probablement bloqué par CORS ou réseau)", error);
            // En cas d'erreur réseau (pas de réponse HTTP), on ne peut pas être sûr, 
            // donc on retourne true pour laisser le script essayer de charger
            this.isValid = true;
            return true;
        } finally {
            this.checkPromise = null;
        }
    }

    /**
     * Force une nouvelle vérification (utile si la clé change dynamiquement, ce qui est rare)
     */
    public reset(): void {
        this.isValid = null;
        this.checkPromise = null;
    }
}

export const googleMapsVerifier = GoogleMapsVerifier.getInstance();
